import { type HttpErrorResponse } from '@angular/common/http';
import { type FormControl, type FormGroup } from '@angular/forms';

import { MultiStepFormSteps, PaymentDetailsInputs, type CaseFormData } from './multi-step-conditional-form.interface';
import { type AkamaiCryptoChallengeErrorResponse } from './response-data.interface';

export type GiftCardBankDetails = Omit<
  CaseFormData[MultiStepFormSteps.PAYMENT_DETAILS],
  PaymentDetailsInputs.COMPENSATION_TYPE
>;

export type GiftCardBankDetailsForm = {
  [key in keyof GiftCardBankDetails]: FormControl<GiftCardBankDetails[key]>;
};

export interface GiftCardContactDetails {
  emailAddress: string;
}

export interface GiftCardConversionPayload {
  body: Partial<GiftCardBankDetails> & GiftCardContactDetails;
  giftCardId: string;
  pin: string;
}

export interface GiftCardConversionForm {
  giftCardPin2: FormControl<GiftCardConversionPayload['pin']>;
}

export type GiftCardConversionError = AkamaiCryptoChallengeErrorResponse | HttpErrorResponse;

export type GiftCardContactDetailsForm = {
  [key in keyof GiftCardContactDetails]: FormControl<GiftCardContactDetails[key]>;
};

export interface GiftCardCustomer {
  email: string;
  firstName: string;
  lastName: string;
  locale: string;
  phone: string;
}

export interface GiftCardReason {
  caseId: string;
  category: string;
  incidentDate: string;
  incidentNumber: string;
  migrated: boolean;
  recLoc: string;
  subCategory: string;
  user: string;
}

/** @todo Verify gift card statuses. */
export enum GiftCardStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  CONVERTED = 'converted',
  EXPIRED = 'expired',
  INITIAL = 'initial',
  LOCKED = 'locked',
}

export interface GiftCardVerificationForm {
  giftCardId: FormControl<GiftCardVerificationPayload['giftCardId']>;
  giftCardPin1: FormControl<GiftCardVerificationPayload['pin']>;
}

export enum GiftCardInformationFormStep {
  GIFT_CARD_VERIFICATION,
  GIFT_CARD_INFORMATION,
  BANK_DETAILS,
  CONTACT_DETAILS,
  GIFT_CARD_CONVERSION,
}

export enum GiftCardInformationFormControls {
  EMAIL_ADDRESS = 'emailAddress',
  GIFT_CARD_ID = 'giftCardId',
  GIFT_CARD_PIN_1 = 'giftCardPin1',
  GIFT_CARD_PIN_2 = 'giftCardPin2',
}

export enum GiftCardInformationFormName {
  BANK_DETAILS_FORM = 'bankDetailsForm',
  CONTACT_DETAILS_FORM = 'contactDetailsForm',
  CONVERSION_FORM = 'conversionForm',
  VERIFICATION_FORM = 'verificationForm',
}

export interface GiftCardInformationForm {
  [GiftCardInformationFormName.BANK_DETAILS_FORM]: FormGroup<GiftCardBankDetailsForm>;
  [GiftCardInformationFormName.CONTACT_DETAILS_FORM]: FormGroup<GiftCardContactDetailsForm>;
  [GiftCardInformationFormName.CONVERSION_FORM]: FormGroup<GiftCardConversionForm>;
  [GiftCardInformationFormName.VERIFICATION_FORM]: FormGroup<GiftCardVerificationForm>;
}

export type GiftCardStepForms = Array<
  | FormGroup<GiftCardBankDetailsForm>
  | FormGroup<GiftCardContactDetailsForm>
  | FormGroup<GiftCardConversionForm>
  | FormGroup<GiftCardInformationForm>
  | FormGroup<GiftCardVerificationForm>
>;

export interface GiftCardVerificationPayload {
  giftCardId: string;
  pin: string;
}

/** @todo Add error response from gift card service. */
export type GiftCardVerificationError = AkamaiCryptoChallengeErrorResponse;

export interface GiftCardVerificationResponse {
  account?: string;
  availableValue: string;
  cashValue?: string;
  currency: string;
  customer: GiftCardCustomer;
  expiredValue?: string;
  expiryDate: string;
  giftCardId: string;
  initialValue: string;
  isConvertibleToCash?: boolean;
  issuanceDate: string;
  reason: GiftCardReason;
  status: GiftCardStatus;
}
